/**
 * Configuración centralizada de rutas
 *
 * Este archivo contiene toda la información relacionada con las rutas de la aplicación:
 * - Paths de las rutas
 * - Títulos para navegación y breadcrumbs
 * - Permisos requeridos
 * - Datos maestros necesarios
 *
 * Principio: Single Source of Truth (SSOT) - Una sola fuente de verdad para todas las rutas
 */

import { PERMISOS } from "@/constants/permissions";
import type { BreadcrumbItemType } from "@/components/ui/breadcrumb";

export interface RouteConfig {
    /** Ruta de la aplicación (ej: "/agreements") */
    path: string;
    /** Título para mostrar en el sidebar */
    title: string;
    /** Etiqueta para el breadcrumb */
    breadcrumbLabel: string;
    /** Permiso requerido para acceder a esta ruta */
    permission: string;
    /** Datos maestros necesarios para esta ruta */
    masterData: string[];
    /** Categoría de la ruta (para agrupar en el sidebar) */
    category?: "gestión" | "negociaciones" | "reportes";
}

/**
 * Configuración de todas las rutas de la aplicación
 */
export const ROUTES: Record<string, RouteConfig> = {
  dashboard: {
    path: "/",
    title: "Dashboard",
    breadcrumbLabel: "Dashboard",
    permission: PERMISOS.MIPRES.VER_GESTION,
    masterData: [],
    category: "gestión",
  },
  dispensation: {
    path: "/dispensation",
    title: "Dispensación",
    breadcrumbLabel: "Gestion mipres",
    permission: PERMISOS.MIPRES.VER_GESTION,
    masterData: [],
    category: "gestión",
  },
  unsyncedOrders: {
    path: "/unsynced",
    title: "Sin sincronizar",
    breadcrumbLabel: "Órdenes sin mipres",
    permission: PERMISOS.MIPRES.ACTUALIZACION_MASIVA_SINCRONIZACION_BOT,
    masterData: [],
    category: "gestión",
  },
  sync: {
    path: "/sync",
    title: "Sincronización",
    breadcrumbLabel: "Sincronización",
    permission: PERMISOS.MIPRES.ACTUALIZACION_MASIVA_SINCRONIZACION_BOT,
    masterData: [],
    category: "gestión",
  },
  serviceSanity: {
    path: "/sanidad",
    title: "Sanidad servicios",
    breadcrumbLabel: "Sanidad de servicios",
    permission: PERMISOS.MIPRES.VER_GESTION,
    masterData: [],
    category: "gestión",
  },
  templateRecipients: {
    path: "/email-recipients",
    title: "Destinatarios correo",
    breadcrumbLabel: "Destinatarios por plantilla",
    permission: PERMISOS.MIPRES.VER_GESTION,
    masterData: [],
    category: "gestión",
  },
  conciliation: {
    path: "/conciliation",
    title: "Conciliación",
    breadcrumbLabel: "Archivos de conciliación",
    permission: PERMISOS.MIPRES.CONCILIACION_EXTERNA,
    masterData: [],
    category: "gestión",
  },
};

/**
 * Obtiene la configuración de una ruta por su path
 */
export function getRouteConfig(path: string): RouteConfig | undefined {
    return Object.values(ROUTES).find((route) => route.path === path);
}

/**
 * Genera el mapa de breadcrumbs desde la configuración de rutas
 */
export function getBreadcrumbMap(): Record<string, BreadcrumbItemType[]> {
    const breadcrumbMap: Record<string, BreadcrumbItemType[]> = {
        "/": [{ label: "Inicio" }, { label: "Facturas" }],
    };

    Object.values(ROUTES).forEach((route) => {
        breadcrumbMap[route.path] = [
            { label: "Inicio", href: "/" },
            { label: route.breadcrumbLabel },
        ];
    });

    return breadcrumbMap;
}

/**
 * Genera el mapa de datos maestros desde la configuración de rutas
 */
export function getMasterDataMap(): Record<string, string[]> {
    const masterDataMap: Record<string, string[]> = {};

    Object.values(ROUTES).forEach((route) => {
        masterDataMap[route.path] = route.masterData;
    });

    return masterDataMap;
}

/**
 * Obtiene todas las rutas de una categoría específica
 */
export function getRoutesByCategory(
    category: "gestión" | "negociaciones"
): RouteConfig[] {
    return Object.values(ROUTES).filter((route) => route.category === category);
}
